import { CardHeader } from '@hue/design-system';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Button,
  Card,
  HueLogoTile,
  Input,
  PasswordStrengthInput,
  Switch,
} from '@hue/design-system';
import { useMutation, useQuery } from '@tanstack/react-query';
import { KeyRound, ShieldCheck } from 'lucide-react';
import { useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { z } from 'zod';
import {
  clientInvitationApi,
  getApiError,
  memberInvitationApi,
  ownerApi,
  type ClientInvitationInspection,
} from '../lib/api.js';
import { AuthenticatorHelp } from './authenticator-help.js';
import { MfaSetup } from './mfa-qr.js';
import { ThemeToggle } from './theme-toggle.js';

const newPasswordSchema = z
  .string()
  .min(8, 'Use ao menos 8 caracteres.')
  .regex(/[A-Z]/, 'Inclua uma letra maiúscula.')
  .regex(/\d/, 'Inclua um número.')
  .regex(/[^A-Za-z0-9]/, 'Inclua um símbolo.');
const codeSchema = z.string().regex(/^\d{6}$/, 'Informe os 6 dígitos.');
type ActivationForm = { password: string; code: string };

export function OwnerActivation({
  token,
  onActivated,
  kind = 'owner',
}: {
  token: string;
  onActivated: () => Promise<void>;
  kind?: 'owner' | 'member' | 'client';
}) {
  const invitation = useQuery({
    queryKey: ['auth', `${kind}-invitation`, token],
    queryFn: () =>
      kind === 'owner'
        ? ownerApi.inspectInvitation(token)
        : kind === 'member'
          ? memberInvitationApi.inspectInvitation(token)
          : clientInvitationApi.inspectInvitation(token),
    retry: false,
  });
  /// Para cliente o código de segurança é opcional (ADR 0067): a conta nova escolhe ligá-lo, e a
  /// conta que já o usa continua informando. Empresa e plataforma sempre pedem.
  const [mfaChosen, setMfaChosen] = useState(false);
  const inspection = invitation.data;
  const accountActive = inspection?.accountActive ?? false;
  const optionalMfa = kind === 'client' && !accountActive;
  const codeNeeded =
    kind !== 'client' ||
    (accountActive
      ? Boolean((inspection as ClientInvitationInspection | undefined)?.mfaRequired)
      : mfaChosen);
  const showQr = Boolean(inspection?.mfaUri) && (!optionalMfa || mfaChosen);
  const form = useForm<ActivationForm>({
    resolver: (values, context, options) =>
      zodResolver(
        z.object({
          password: accountActive ? z.string().min(1, 'Informe sua senha.') : newPasswordSchema,
          code: codeNeeded ? codeSchema : z.string(),
        }),
      )(values, context, options),
    defaultValues: { password: '', code: '' },
  });
  const activate = useMutation({
    mutationFn: (data: ActivationForm) => {
      const input = { token, password: data.password, code: data.code };
      return kind === 'owner'
        ? ownerApi.activate(input)
        : kind === 'member'
          ? memberInvitationApi.activate(input)
          : clientInvitationApi.activate({
              token,
              password: data.password,
              ...(codeNeeded ? { code: data.code } : {}),
            });
    },
    onSuccess: onActivated,
  });
  const passwordField = form.register('password');
  const passwordValue = useWatch({ control: form.control, name: 'password' });

  return (
    <div className="relative min-h-screen overflow-hidden bg-surface px-4 py-10 sm:py-14">
      <div className="pointer-events-none absolute inset-0">
        <div className="absolute left-1/2 top-0 h-80 w-80 -translate-x-1/2 rounded-full bg-brand-500/[.08] blur-3xl" />
      </div>
      <div className="absolute right-4 top-4 sm:right-6 sm:top-6">
        <ThemeToggle />
      </div>

      <div className="relative mx-auto w-full max-w-2xl">
        <div className="mb-8 text-center">
          <HueLogoTile className="mx-auto mb-4 size-14 rounded-2xl" />
          <p className="label-overline text-brand-400">HUE•System</p>
          <h1 className="mt-2 text-3xl font-bold tracking-[-.045em]">Ative seu acesso</h1>
          <p className="mt-2 text-sm text-content-muted">
            {invitation.data
              ? kind === 'owner'
                ? `Você será o proprietário de ${invitation.data.tenant.displayName}.`
                : kind === 'client'
                  ? `Você terá acesso ao portal de ${(invitation.data as ClientInvitationInspection).client.displayName}.`
                  : `Você terá acesso a ${invitation.data.tenant.displayName}.`
              : invitation.error
                ? kind === 'owner'
                  ? 'Solicite um novo convite ao administrador da plataforma.'
                  : 'Solicite um novo convite à pessoa responsável pela empresa.'
                : 'Validando o convite seguro…'}
          </p>
        </div>

        {invitation.isLoading ? (
          <Card className="grid min-h-64 place-items-center bg-surface-card">
            <div
              className="size-8 animate-spin rounded-full border-2 border-brand-500/20 border-t-brand-500"
              aria-label="Validando convite"
            />
          </Card>
        ) : invitation.error || !invitation.data ? (
          <Card className="border-danger/30 bg-surface-card text-center">
            <ShieldCheck className="mx-auto size-9 text-danger" />
            <h2 className="mt-4 text-lg font-semibold">Convite indisponível</h2>
            <p role="alert" className="mt-2 text-sm text-content-secondary">
              {getApiError(invitation.error)}
            </p>
          </Card>
        ) : (
          <Card className="overflow-hidden border-surface-border bg-surface-card p-0">
            <div className="px-6 py-5 sm:px-8">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <CardHeader
                    title={`${invitation.data.user.firstName} ${invitation.data.user.lastName}`}
                    icon={<ShieldCheck className="size-4" />}
                  />
                  <p className="text-xs text-content-muted">{invitation.data.user.email}</p>
                </div>
                <AuthenticatorHelp />
              </div>
            </div>

            <form
              noValidate
              className="grid gap-7 p-6 sm:grid-cols-[210px_minmax(0,1fr)] sm:p-8"
              onSubmit={(event) => void form.handleSubmit((data) => activate.mutate(data))(event)}
            >
              <div>
                {showQr && invitation.data.mfaUri ? (
                  <MfaSetup uri={invitation.data.mfaUri} />
                ) : (
                  <div className="grid aspect-square place-items-center rounded-2xl border border-brand-500/20 bg-brand-500/[.06] p-5 text-center">
                    <ShieldCheck className="size-9 text-brand-400" />
                    <p className="mt-3 text-sm leading-5 text-content-secondary">
                      {codeNeeded
                        ? 'Use o autenticador já vinculado à sua conta.'
                        : 'O código de segurança é opcional para quem agenda como cliente.'}
                    </p>
                  </div>
                )}
                {showQr ? null : (
                  <p className="mt-3 text-center text-xs leading-5 text-content-muted">
                    Confirme sua identidade.
                  </p>
                )}
              </div>

              <div className="space-y-4">
                {invitation.data.accountActive ? (
                  <Input
                    label="Sua senha atual"
                    type="password"
                    autoComplete="current-password"
                    error={form.formState.errors.password?.message}
                    {...passwordField}
                  />
                ) : (
                  <PasswordStrengthInput
                    label="Crie sua senha"
                    autoComplete="new-password"
                    name="password"
                    onBlur={() => void form.trigger('password')}
                    value={passwordValue}
                    onValueChange={(value) =>
                      form.setValue('password', value, {
                        shouldDirty: true,
                        shouldTouch: true,
                        shouldValidate: true,
                      })
                    }
                    error={form.formState.errors.password?.message}
                  />
                )}
                {optionalMfa ? (
                  <Switch checked={mfaChosen} onChange={setMfaChosen}>
                    Ligar o código de segurança no aplicativo autenticador (opcional)
                  </Switch>
                ) : null}
                {codeNeeded ? (
                  <Input
                    label="Código de autenticação"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    maxLength={6}
                    placeholder="000000"
                    leftIcon={<KeyRound className="size-4" />}
                    error={form.formState.errors.code?.message}
                    {...form.register('code')}
                  />
                ) : null}
                {activate.error ? (
                  <div
                    role="alert"
                    className="rounded-xl border border-danger/35 bg-danger/10 px-4 py-3 text-sm text-danger"
                  >
                    {getApiError(activate.error)}
                  </div>
                ) : null}
                <Button className="w-full" size="lg" loading={activate.isPending}>
                  Ativar e entrar
                </Button>
              </div>
            </form>
          </Card>
        )}
      </div>
    </div>
  );
}
